<template>
    <div class="rule-action-webhooks-form">
        <div class="stack-m">
            <div class="field">
                <label class="field-label">Webhook URL</label>
                <div class="field-group">
                    <v-select
                        class="http-verbs no-flex"
                        :options="httpVerbsList"
                        options-label="label"
                        options-key="key"
                        placeholder="Select HTTP Verb"
                        :value="webhook.method"
                        @input="value => updateWebHook('method', value)"
                        :validate="$v.webhook.method"
                    />
                    <r-input
                        class="input-url"
                        :value="webhook.url"
                        @input="value => updateWebHook('url', value)"
                        :validate="$v.webhook.url"
                        :disabled="useCredentialHash"
                        placeholder="Enter the webhook URL"/>
                </div>
            </div>
            <span class="field-caption">The webhook URL must include http or https. e.g. https://www.mywebhook.com/endpoint</span>
        </div>
        <r-tabs :divided="true" content-class="no-bottom-inset">
            <r-tab name="Authorization" :active="true">
                <div class="grid-content">
                    <div v-if="isFormUpdating && hasCredentialHash && !useCredentialHash"
                         class="grid-item grid-item-full">
                        <r-checkbox label="Use previously created credentials"
                                    :selected="useCredentialHash"
                                    :value="true"
                                    caption="Enable to use existing credentials. Uncheck to update or create a new login."
                                    @input="toggleCredentialHashChecked"/>
                        <div class="divider"/>
                    </div>
                    <div v-if="isFormUpdating && useCredentialHash" class="grid-item grid-item-full">
                        <r-checkbox label="Use previously created credentials"
                                    :selected="useCredentialHash"
                                    :value="true"
                                    caption="Enable to use existing credentials. Uncheck to update or create a new login."
                                    @input="toggleCredentialHashChecked"/>
                    </div>
                    <div v-if="!useCredentialHash"
                         class="grid-item grid-item-half grid-item-break-half">
                        <div class="field" :class="{'is-error': $v.webhookCredential.auth.type.$error}">
                            <div class="fiel d-control has-icon-right">
                                <v-select
                                    label="Authorization Type"
                                    :options="authorizationTypes"
                                    option-label="value"
                                    option-key="key"
                                    placeholder="Type to search or select"
                                    :value="webhookCredential.auth.type"
                                    @input="value => updateAuth('type', value)"
                                    :validate="$v.webhookCredential.auth.type"
                                />
                            </div>
                        </div>
                    </div>
                    <template v-if="!useCredentialHash">
                        <template
                            v-if="webhookCredential.auth.isDigestAuth || webhookCredential.auth.isBasicAuth">
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    :value="webhookCredential.auth.username"
                                    @input="value => updateAuth('username', value)"
                                    :validate="$v.webhookCredential.auth.username"
                                    label="Username"/>
                            </div>
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    :value="webhookCredential.auth.password"
                                    @input="value => updateAuth('password', value)"
                                    :validate="$v.webhookCredential.auth.password" :password="true"
                                    label="Password"/>
                            </div>
                        </template>
                        <template v-else-if="webhookCredential.auth.isOAuth1">
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    :value="webhookCredential.auth.consumerKey"
                                    @input="value => updateAuth('consumerKey', value)"
                                    :validate="$v.webhookCredential.auth.consumerKey"
                                    label="Consumer Key"/>
                            </div>
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    :value="webhookCredential.auth.consumerSecret"
                                    @input="value => updateAuth('consumerSecret', value)"
                                    :validate="$v.webhookCredential.auth.consumerSecret"
                                    label="Consumer Secret"/>
                            </div>
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    :value="webhookCredential.auth.token"
                                    @input="value => updateAuth('token', value)"
                                    :validate="$v.webhookCredential.auth.token" label="Token"/>
                            </div>
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    :value="webhookCredential.auth.tokenSecret"
                                    @input="value => updateAuth('tokenSecret', value)"
                                    :validate="$v.webhookCredential.auth.tokenSecret"
                                    label="Token Secret"/>
                            </div>
                        </template>
                    </template>
                </div>
            </r-tab>
            <r-tab :name="headersTabLabel">
                <ul class="list list-group list-divided stack-m">
                    <li class="list-item align-items-center" v-for="(header, index) in webhook.headers"
                        v-bind:key="index">
                        <v-toggle
                            class="inline-m no-flex"
                            :value="true"
                            :selected="header.isActive"
                            @input="toggleHeadersIsActive(index)"
                        />
                        <div class="field list-item-content inline-m">
                            <r-input
                                :value="header.name"
                                @input="value => updateHeader(index, `name`, value)"
                                :validate="$v.webhook.headers.$each[index].name"
                                placeholder="Name"/>
                        </div>
                        <div class="field list-item-content inline-m">
                            <r-input
                                :value="header.value"
                                @input="value => updateHeader(index, `value`, value)"
                                :validate="$v.webhook.headers.$each[index].value"
                                placeholder="Value"/>
                        </div>
                        <span class="list-item-content no-flex">
                                    <v-icon-button type="basic" @click="deleteHeader(index)"><v-icon
                                        icon="trash"/></v-icon-button>
                                </span>
                    </li>
                    <r-button type="link" :class="{'add-header': webhook.headers.length > 0}"
                              @click="addHeader">Add header
                    </r-button>
                </ul>
            </r-tab>
            <r-tab name="Body">
                <r-input
                    :value="webhook.body"
                    @input="value => updateWebHook(`body`, value)"
                    :multiline="true"
                    rows="5"/>
            </r-tab>
            <r-tab :name="queryTabLabel">
                <ul class="list list-group list-divided">
                    <li class="list-item align-items-center" v-for="(item, i) in webhookSortedQuery"
                        v-bind:key="i">
                        <div class="field list-item-content inline-m">
                            <r-input
                                :value="item.key"
                                @input="value => updateQuery(i, value, item.value)"
                                placeholder="Key"
                                :validate="$v.webhook.query[item.key]"
                            />
                        </div>
                        <div class="field list-item-content inline-m">
                            <r-input
                                :value="item.value"
                                @input="value => updateQuery(i, item.key, value)"
                                placeholder="Value"
                                :validate="$v.webhook.query[item.key]"
                            />
                        </div>
                        <span class="list-item-content no-flex">
                            <v-icon-button type="basic" @click="deleteQuery(i)"><v-icon icon="trash"/></v-icon-button>
                        </span>
                    </li>
                    <r-button type="link" @click="addQuery" :class="{'add-header': webhookSortedQuery.length > 0}">Add
                        Query
                    </r-button>
                </ul>
            </r-tab>
        </r-tabs>
        <preview-webhook-view
            v-if="isPreviewWebhookOpened"
            :is-loading="isLoading"
            :webhook="webhook"
            v-on:cancel="hidePreviewWebhook"
            v-on:test-webhook="testWebhook"/>
        <div class="divider divider-tile-content"></div>
        <div>
            <r-button type="default" :disabled="isFormDisabled" @click="openPreviewWebhook">Test Webhook
            </r-button>
        </div>
    </div>
</template>

<script>
    import {required, url} from 'vuelidate/lib/validators';
    import vTitle from '@/components/v-title';
    import PreviewWebhookView from '@/components/webhook/preview-webhook-view';
    import placeholderPattern from '@/components/webhook/placeholder-pattern';

    import {
        vErrorMessages,
        vIcon,
        vIconButton,
        vRadio,
        vSelect,
        vToggle,
    } from '@/components/form';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import {WebhookHeaderModel} from '@/common/models/rules/actions/trigger-webhook-action-model';
    import {HttpVerbsList} from './http-verbs';
    import {AuthorizationTypesList} from './authorization-types';

    export default {
        name: 'webhook-endpoint-form',
        components: {
            vTitle,
            vIcon,
            vIconButton,
            vErrorMessages,
            vInlineNotifications,
            vRadio,
            vSelect,
            vToggle,
            PreviewWebhookView,
        },
        props: {
            isFormUpdating: {
                type: [Boolean, undefined],
                required: true,
            },
            isLoading: {
                type: Boolean,
                default: () => false,
            },
            /**
             * @type {WebhookModel|{}}
             */
            webhook: Object,
            /**
             * @type {WebhookCredentialModel|{}}
             */
            webhookCredential: Object,

        },
        validations() {
            const validation = {
                webhook: {
                    method: {
                        required,
                    },
                    url: {
                        required,
                        url(value) {
                            return placeholderPattern.test(value)
                                ? url(value.replace(placeholderPattern, ''))
                                : url(value);
                        },
                    },
                    headers: {
                        $each: {
                            name: {
                                required,
                            },
                            value: {
                                required,
                            },
                        },
                    },
                    query: {
                        ...Object.keys(this.webhook.query || {}).reduce((memo, key) => ({
                            ...memo,
                            [key]: {
                                required,
                                isUnique: () => this.webhookSortedQuery
                                    .filter(item => item.key === key).length === 1,
                            },
                        }), {}),
                    },
                },
                webhookCredential: {
                    auth: {
                        type: {
                            required,
                        },
                    },
                },
            };
            if (this.useCredentialHash) {
                validation.webhookCredential = {};
            } else {
                if (this.webhookCredential.auth.isBasicAuth
                    || this.webhookCredential.auth.isDigestAuth) {
                    validation.webhookCredential.auth = {
                        ...validation.webhookCredential.auth,
                        username: {required},
                        password: {required},
                    };
                }
                if (this.webhookCredential.auth.isOAuth1) {
                    validation.webhookCredential.auth = {
                        ...validation.webhookCredential.auth,
                        consumerKey: {required},
                        consumerSecret: {required},
                        token: {required},
                        tokenSecret: {required},
                    };
                }
            }
            return validation;
        },
        data() {
            return {
                isPreviewWebhookOpened: false,
                authorizationTypes: [...AuthorizationTypesList],
                useCredentialHash: !!this.webhook.credentialHash,
                httpVerbsList: [...HttpVerbsList],
                webhookSortedQuery: Object.keys(this.webhook.query || {})
                    .sort()
                    .map(key => ({key, value: this.webhook.query[key]})),
            };
        },
        computed: {
            isFormDisabled() {
                return this.isLoading;
            },
            headersTabLabel() {
                const slug = this.webhook.headers.length
                    ? ` (${this.webhook.headers.length})`
                    : '';
                return `Headers${slug}`;
            },
            queryTabLabel() {
                const queryLength = Object.keys(this.webhook.query).length;
                const slug = queryLength
                    ? ` (${queryLength})`
                    : '';
                return `Query${slug}`;
            },
            hasCredentialHash() {
                return !!this.webhook.credentialHash;
            },
        },
        methods: {
            handleUpdateWebhookQuery() {
                const query = this.webhookSortedQuery.reduce((memo, item) => ({
                    ...memo,
                    [item.key]: item.value,
                }), {});
                this.onInput('webhook.query', query);
            },
            updateWebHook(term, value) {
                this.onInput(`webhook.${term}`, value);
            },
            updateAuth(term, value) {
                this.onInput(`webhookCredential.auth.${term}`, value);
            },
            updateHeader(index, term, value) {
                this.onInput(`webhook.headers.${index}.${term}`, value);
            },
            updateQuery(index, key, value) {
                this.webhookSortedQuery.splice(index, 1, {key, value});
                this.handleUpdateWebhookQuery();
            },
            toggleCredentialHashChecked() {
                this.useCredentialHash = !this.useCredentialHash;
            },
            toggleWebhookIsActive() {
                this.onInput('webhook.isActive', !this.webhook.isActive);
            },
            toggleHeadersIsActive(index) {
                const header = this.webhook.headers[index];
                header.status = header.isActive
                    ? WebhookHeaderModel.ToggleStatuses.inactive.name
                    : WebhookHeaderModel.ToggleStatuses.active.name;
                this.onInput(`webhook.headers.${index}`, header);
            },
            addHeader() {
                const header = new WebhookHeaderModel({
                    status: WebhookHeaderModel.ToggleStatuses.active.name,
                    name: '',
                    value: '',
                });
                this.webhook.headers.push(header);
                this.onInput(`webhook.headers`, this.webhook.headers);
            },
            addQuery() {
                this.webhookSortedQuery.push({key: '', value: ''});
                this.handleUpdateWebhookQuery();
            },
            deleteHeader(index) {
                this.webhook.headers.splice(index, 1);
            },
            deleteQuery(index) {
                this.webhookSortedQuery.splice(index, 1);
                this.handleUpdateWebhookQuery();
            },
            resetAuth() {
                this.webhookCredential.auth.username = '';
                this.webhookCredential.auth.password = '';
                this.webhookCredential.auth.consumerKey = '';
                this.webhookCredential.auth.consumerSecret = '';
                this.webhookCredential.auth.token = '';
                this.webhookCredential.auth.tokenSecret = '';
            },
            openPreviewWebhook() {
                this.$v.$touch();
                if (!this.$v.webhook.$error && !this.$v.webhookCredential.$error) {
                    this.isPreviewWebhookOpened = true;
                }
            },
            hidePreviewWebhook() {
                this.isPreviewWebhookOpened = false;
            },
            async testWebhook(data) {
                this.$v.$touch();
                if (!this.$v.webhook.$error && !this.$v.webhookCredential.$error) {
                    this.$emit('test', data);
                }
            },
            onInput(key, value) {
                if (key === 'webhookCredential.auth.type') {
                    this.resetAuth();
                }
                this.$emit('input', {key, value});
            },
        },
    };
</script>
